/**
 * Inline versions of driver-marker.svg / car-marker.svg. They're inlined rather than
 * loaded as <img src>, so the glyph and the ring can be tinted with the entity's status
 * colour at runtime. The dark badge behind the glyph stays fixed so the marker keeps
 * enough contrast against both the roadmap and satellite tiles.
 */

const BADGE_FILL = '#292929';
const SIZE = 30;

type Props = {
  color: string;
};

/** Ring around the badge — shared by both marker shapes. */
function Ring({ color }: Props): JSX.Element {
  return (
    <rect
      x="0.51573"
      y="1.50695"
      width="31"
      height="31"
      rx="15.5"
      transform="rotate(-1.83218 0.51573 1.50695)"
      fill="none"
      stroke={color}
    />
  );
}

function Badge(): JSX.Element {
  return (
    <rect
      x="0.51573"
      y="1.50695"
      width="31"
      height="31"
      rx="15.5"
      transform="rotate(-1.83218 0.51573 1.50695)"
      fill={BADGE_FILL}
    />
  );
}

/** Arrow marker: shown when the entity has a heading (more than one location point). */
export function HeadingMarkerIcon({ color }: Props): JSX.Element {
  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox="0 0 34 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Vehicle marker"
    >
      <Badge />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.2087 23.1448C10.1191 23.0481 10.0602 22.927 10.0394 22.7968C10.0186 22.6666 10.0369 22.5332 10.0919 22.4134L15.7051 10.2277C15.7569 10.1151 15.839 10.0191 15.9422 9.95053C16.0455 9.88196 16.1658 9.84353 16.2897 9.83957C16.4136 9.83561 16.5361 9.86626 16.6436 9.92809C16.751 9.98992 16.8391 10.0805 16.8979 10.1896L23.2785 21.9916C23.3412 22.1076 23.3682 22.2397 23.3559 22.371C23.3436 22.5023 23.2926 22.627 23.2094 22.7294C23.1262 22.8317 23.0145 22.9071 22.8885 22.9459C22.7624 22.9848 22.6277 22.9854 22.5013 22.9476L17.3266 21.4043L17.2118 17.8148C17.2062 17.6381 17.1305 17.4708 17.0016 17.3499C16.8726 17.2289 16.7009 17.1641 16.5242 17.1698C16.3475 17.1754 16.1802 17.2511 16.0593 17.38C15.9383 17.509 15.8735 17.6807 15.8792 17.8574L15.994 21.4469L10.9286 23.3185C10.8049 23.364 10.6705 23.3719 10.5424 23.3411C10.4143 23.3103 10.2981 23.2415 10.2087 23.1448Z"
        fill={color}
      />
      <Ring color={color} />
    </svg>
  );
}

/** Car marker: shown when the entity has a single location point (no heading to render). */
export function CarMarkerIcon({ color }: Props): JSX.Element {
  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox="0 0 34 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Vehicle marker"
    >
      <Badge />
      <path
        d="M2 7.83342L3.65333 3.97475C3.75622 3.73475 3.9273 3.53024 4.14537 3.3866C4.36344 3.24296 4.61888 3.16652 4.88 3.16675H11.1213C11.3822 3.16678 11.6374 3.24335 11.8552 3.38697C12.073 3.53059 12.2439 3.73496 12.3467 3.97475L14 7.83342M2 7.83342H14M2 7.83342V12.5001M14 7.83342V12.5001M12 12.5001V12.8334C12 13.0986 12.1054 13.353 12.2929 13.5405C12.4804 13.7281 12.7348 13.8334 13 13.8334C13.2652 13.8334 13.5196 13.7281 13.7071 13.5405C13.8946 13.353 14 13.0986 14 12.8334V12.5001M12 12.5001H4M12 12.5001H14M4 12.5001V12.8334C4 13.0986 3.89464 13.353 3.70711 13.5405C3.51957 13.7281 3.26522 13.8334 3 13.8334C2.73478 13.8334 2.48043 13.7281 2.29289 13.5405C2.10536 13.353 2 13.0986 2 12.8334V12.5001M4 12.5001H2M4.66667 9.83342H4.66733M11.3333 9.83342H11.334"
        stroke={color}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="translate(6.3, 6.3) scale(1.25, 1.25)"
      />
      <Ring color={color} />
    </svg>
  );
}
